'use client'
import { useEffect, useRef } from 'react'
import { FaTimes } from 'react-icons/fa'
import Portal from './Portal'

const Modal = ({
  isOpen,
  closeModal,
  title,
  content,
  confirmation,
  confirmation2, // second confirmation button prop
  onConfirm2, // second confirmation action prop
  width,
  height,
  isConfirmDisabled,
  isConfirm2Disabled,
  onConfirm,
  scrollable,
  // Opt-in, for a modal mounted only while open: names the panel as a dialog (`role="dialog"`, `aria-modal`, this
  // label), closes it on Escape, moves focus into it, keeps Tab inside it and gives focus back to whatever had it
  // when it closes. Without it the modal behaves as it always has.
  dialogLabel,
}) => {
  const panel = useRef(null)
  // Read through a ref, so a caller's new closeModal on each render never re-runs the dialog effect (and refocuses).
  const close = useRef(closeModal)
  close.current = closeModal
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset'
    // Restore on unmount as well as on close. Navigating away while a modal is
    // still open — the cancel flow does exactly this on "Stay Subscribed" —
    // would otherwise leave the destination page permanently unscrollable.
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !dialogLabel) return undefined
    const opener = document.activeElement
    const focusable = () =>
      Array.from(
        panel.current?.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      )
    // The panel renders a little later (Portal mounts first), so focus moves in once it is there.
    let frame = 0
    let tries = 0
    const focusIn = () => {
      if (panel.current) focusable()[0]?.focus()
      else if (tries++ < 30) frame = requestAnimationFrame(focusIn)
    }
    frame = requestAnimationFrame(focusIn)
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close.current()
        return
      }
      if (e.key !== 'Tab') return
      const items = focusable()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      const inside = panel.current?.contains(document.activeElement)
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKeyDown)
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [isOpen, dialogLabel])

  return (
    // z-[110] = modal/dialog tier. Must sit ABOVE the fixed ConsentBanner, side
    // drawers and ImageLightbox (z-[100]) and the header (z-50). Keep all dialog
    // overlays on this tier.
    // Portaled to <body> so the overlay is never trapped inside an ancestor
    // stacking context (e.g. the create-view's `fixed z-30` container).
    <Portal>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeModal()
          }
        }}
        // Bottom padding = the on-screen keyboard, so the flex centring below
        // happens inside the *visible* area. Without it a modal with a textarea
        // centres in the layout viewport and sits under the keyboard the moment
        // the field is focused. 0px whenever no keyboard is up.
        style={{ paddingBottom: 'var(--pc-keyboard-inset, 0px)' }}
        className={`fixed z-[110] top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center ${
          isOpen ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-200 ${
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        {/* Hardened container: a single coherent height cap (max-h-[85vh]) +
          overflow-hidden so no child can ever paint outside the rounded box,
          a shrink-0 header/footer, and an always-scrollable <main>. This makes
          EVERY consumer overflow-safe regardless of how much content it renders
          — the `scrollable` prop is now a no-op (scrolling is always on). */}
        <div
          ref={panel}
          {...(dialogLabel
            ? { role: 'dialog', 'aria-modal': true, 'aria-label': dialogLabel }
            : {})}
          // max-h clamps to whichever is smaller: the usual 85vh, or the
          // padded container itself — so when the keyboard shortens that
          // container the panel shrinks with it instead of overflowing.
          className={`bg-[var(--middle-bg-color)] m-4 md:m-12 overflow-hidden flex flex-col min-h-0 max-h-[min(85vh,100%)] rounded-lg shadow-2xl p-6 text-gray-100 space-y-4 relative transform transition-all duration-200 ${
            isOpen ? 'scale-100 opacity-100' : 'scale-70 opacity-0'
          } ${height ? 'h-5/6' : ''} ${
            width ? 'max-w-lg md:max-w-full' : 'max-w-lg md:max-w-2xl'
          }`}
        >
          <header className="shrink-0">
            <h2 className="text-xl font-semibold text-shadow">{title}</h2>
            <button
              onClick={closeModal}
              className="absolute right-2 top-2 m-4 text-white"
              style={{ fontSize: '1.5rem' }}
              aria-label="Close modal"
            >
              <FaTimes />
            </button>
          </header>
          {title && <hr className="border-gray-800 shrink-0" />}
          {/* Always scrollable + min-h-0 so tall content scrolls INSIDE the box.
            my-auto keeps short content vertically centered (as before) but
            collapses when content overflows, keeping the top reachable. */}
          <main className="flex-1 min-h-0 overflow-y-auto flex flex-col space-y-4">
            <div className="text-lg my-auto">{content}</div>
          </main>

          {(confirmation || confirmation2) && (
            <footer className="shrink-0">
              {confirmation && (
                <button
                  onClick={() => {
                    closeModal()
                    if (!isConfirmDisabled && onConfirm) {
                      onConfirm()
                    }
                  }}
                  className={`bg-[var(--main-bg-color)] text-white rounded-full py-2 px-4 w-full font-semibold transition-colors duration-200 mt-6 ${
                    isConfirmDisabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isConfirmDisabled}
                >
                  {confirmation}
                </button>
              )}
              {confirmation2 && (
                <button
                  onClick={() => {
                    closeModal()
                    if (!isConfirm2Disabled && onConfirm2) {
                      onConfirm2()
                    }
                  }}
                  className={`bg-[var(--foreground-bg-color)] text-white rounded-full py-2 px-4 w-full font-semibold transition-colors duration-200 mt-6 ${
                    isConfirm2Disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isConfirm2Disabled}
                >
                  {confirmation2}
                </button>
              )}
            </footer>
          )}
        </div>
      </div>
    </Portal>
  )
}

export default Modal
